import axios, { Method } from 'axios';
import { handleTokenRefresh } from 'src/utils/authService';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (!error.response) {
      toast.error('Unable to connect to the server. Please try again later.');
      return Promise.reject({
        message: 'Network error. Please check your internet connection.',
        type: 'NETWORK_ERROR'
      });
    }

    const statusCode = error.response.status;

    // Handle 401 Unauthorized errors (token expiration)
    if (statusCode === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Attempt token refresh using the cookie-stored refresh token
        const newAccessToken = await handleTokenRefresh(); // handleTokenRefresh uses cookies internally
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`; // Optional, in case manual header is needed
        return await axiosInstance(originalRequest);
      } catch (refreshError) {
        toast.error('Session expired. Please log in again.');
        localStorage.clear();

        // Extract state, township, uuid from current URL path
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const [state, township, uuid] = pathParts;

        // Redirect safely
        if (state && township && uuid) {
          window.location.href = `/${state}/${township}/${uuid}/login`;
        } else {
          window.location.href = '/'; // fallback if somehow invalid
        }
        return Promise.reject(refreshError);
      }
    }

    // 403 Forbidden errors
    if (statusCode === 403) {
      toast.error('You are not authorized to access this resource.');
      return Promise.reject(error);
    }

    // Handle 500+ Server errors
    if (statusCode >= 500) {
      toast.error('Server error. Please try again later.');
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Makes an HTTP request using Axios.
 * @param method - The HTTP method (e.g., "GET", "POST").
 * @param endpoint - The API endpoint (relative to the base URL).
 * @param data - The request payload.
 * @param headers - Optional headers to include in the request.
 * @returns The response data from the API.
 */
export const httpRequest = async (
  method: Method,
  endpoint: string,
  data: any = {},
  headers: any = {}
) => {
  try {
    const response = await axiosInstance({
      method,
      url: `${endpoint}`,
      data,
      headers: {
        ...headers,
        'x-page-url': window.location.href // add browser page URL here
      }
    });

    // If the response is empty, handle it gracefully (avoid error propagation)
    if (response.data === null || response.data === undefined) {
      return {}; // Return an empty object for empty responses
    }
    return response.data;
  } catch (error: any) {
    if (error.response) {
      // Handle HTTP error responses
      console.error(`HTTP error: ${error.response.status}`);
      return (
        error.response.data || {
          message: 'An error occurred during the request'
        }
      );
    } else {
      // Handle network or other errors
      console.error('Network error or unable to connect to the server.');
      return {
        message: 'Unable to connect to the server. Please try again later.',
        type: 'NETWORK_ERROR'
      };
    }
  }
};
